export default {
  en: {
    title: "Administration",
    subtitle: "For the whole platform: tools, people and their roles, organisation settings and platform settings.",
    orgSubtitle: "For {{org}}: people and their roles, approval amounts and how your organisation's data is handled.",
    platformTeam: "Platform team",
    tab: { tools: "Tools", users: "Users & roles", tenant: "Organisation", approvals: "Approval amounts", spending: "Spending limits", platform: "Platform settings", internet: "Internet access", web: "Web browser", command: "Profiles" },
    yes: "Yes",
    no: "No",
    save: "Save",
    cancel: "Cancel",
    issues: {
      json_invalid: "The export is not valid JSON",
      tools_missing: "The export has no \"tools\" object with at least one tool",
      version_invalid: "Version must look like 1.0.0",
      owner_required: "Owner is required",
      note_short: "Write a note of at least 3 characters",
      title_required: "Enter the title in Arabic and English",
      host_invalid: "Egress entries are lower-case host names without scheme, port or path: {{host}}",
      mcp_name_invalid: "Each local MCP server needs a short name: lower-case letters, digits and -",
      mcp_name_duplicate: "Two local MCP servers have the same name",
      mcp_image_invalid: "A local MCP server's image must be pinned by digest (…@sha256: and 64 characters)",
      mcp_command_missing: "Each local MCP server needs the command to start it",
      tier_invalid: "Minimum approval tier must be 1 to 4",
      rows_invalid: "Retrieval row limit must be a whole number from 1 to 10000",
      threshold_invalid: "Thresholds: amount must be 0 or more and confidence between 0 and 1",
      username_invalid: "User name: 3 to 63 lower-case letters, digits, dots, hyphens or underscores, starting with a letter or digit",
      email_invalid: "Enter a valid e-mail address",
      name_required: "Enter the first and last name",
    },
    users: {
      showDisabled: "Show switched-off accounts",
      intro: "People who sign in to the console. Changes take effect at the user's next sign-in; removing roles, disabling an account and resetting a password sign the user out. Every change is checked by policy and audited.",
      tenant: "Organisation",
      search: "Search by name, user name or e-mail",
      newUser: "New user",
      list: "Users",
      total: "{{n}} user(s)",
      none: "No users match.",
      user: "User",
      username: "User name",
      email: "E-mail",
      status: "Status",
      enabled: "Active",
      disabled: "Disabled",
      roles: "Roles",
      clearance: "Clearance",
      locale: "Language",
      actions: "Actions",
      you: "you",
      noRoles: "No roles",
      edit: "Edit {{name}}",
      editRoles: "Change roles of {{name}}",
      resetPassword: "Reset password of {{name}}",
      createTitle: "New user",
      editTitle: "Edit {{name}}",
      rolesTitle: "Roles of {{name}}",
      resetTitle: "Reset password of {{name}}",
      firstName: "First name",
      lastName: "Last name",
      usernameHelp: "Lower case; cannot be changed later.",
      enabledLabel: "Account active",
      localeName: { ar: "Arabic", en: "English" },
      create: "Create user",
      save: "Save",
      created: "User {{name}} created",
      saved: "User {{name}} saved",
      rolesSaved: "Roles of {{name}} saved",
      initialRoles: "Roles",
      passwordTitle: "Temporary password for {{name}}",
      passwordOnce: "Shown only once. Give it to the user through a secure channel; they must choose a new password at the next sign-in.",
      copy: "Copy password",
      copied: "Password copied",
      done: "Done",
      resetBody: "A new temporary password is generated. The user is signed out everywhere and must choose a new password at the next sign-in.",
      resetOk: "Reset password",
      selfRoles: "You cannot change your own roles; ask another administrator.",
      selfDisable: "You cannot disable your own account.",
      selfReset: "Change your own password in the account console.",
      platformOnly: "Platform roles are granted only by a platform administrator, and only to users of the Platform team.",
      otherRoles: "Also holds (not managed here): {{roles}}",
      signOutNote: "Removing a role signs the user out of every session.",
      area: { work: "Work", build: "Build", govern: "Govern", observe: "Observe", channels: "Channels", platform: "Platform" },
      roleName: {
        approver: "Approver",
        reviewer: "Reviewer",
        "case-worker": "Case worker",
        "case-supervisor": "Case supervisor",
        finance: "Finance team",
        "finance-approver": "Finance approver",
        "finance-director": "Finance director",
        cfo: "Chief financial officer",
        builder: "Agent builder",
        "tool-owner": "Tool owner",
        "retrieval-curator": "Knowledge curator",
        "eval-curator": "Test curator",
        "memory-curator": "Memory curator",
        "data-steward": "Data steward",
        "release-approver": "Release approver",
        "agent-admin": "Agent administrator",
        "model-admin": "Model administrator",
        "guardrails-admin": "Guardrails administrator",
        "case-admin": "Case administrator",
        "privacy-officer": "Privacy officer",
        "pii-reader": "Personal data reader",
        "kill-switch-operator": "Emergency stop operator",
        "tenant-admin": "Organisation administrator",
        "rules-admin": "Rules administrator",
        auditor: "Auditor",
        operator: "Operator",
        executive: "Executive",
        "channel-agent": "Support agent",
        "channel-supervisor": "Support supervisor",
        "channel-admin": "Channel administrator",
        "channel-consent-admin": "Consent administrator",
        "channel-campaigner": "Notification sender",
        "platform-admin": "Platform administrator",
        "platform-auditor": "Platform auditor",
        "tool-admin": "Tool administrator",
        "code-runner": "Code runner user",
        "marketplace-curator": "Marketplace curator",
      },
      roleDesc: {
        approver: "Signs human approvals in the reviewer inbox",
        reviewer: "Works the reviewer inbox and cases",
        "case-worker": "Works cases and tasks",
        "case-supervisor": "Supervises queues, reassigns and bulk-decides tasks",
        finance: "Example business role used by reference agents",
        "finance-approver": "Approves finance actions of reference agents",
        "finance-director": "Signs the middle level of large approvals",
        cfo: "Signs the top level of large approvals",
        builder: "Builds and tests agents; requests releases",
        "tool-owner": "Owns tool registrations",
        "retrieval-curator": "Curates knowledge collections",
        "eval-curator": "Curates feedback into evaluation datasets",
        "memory-curator": "Writes organisation-wide knowledge and memory",
        "data-steward": "Sees all business data rows within the organisation",
        "release-approver": "Approves releases (four-eyes)",
        "agent-admin": "Manages agent identities",
        "model-admin": "Models: providers, routing and spending limits",
        "guardrails-admin": "Administers guardrail configurations",
        "case-admin": "Administers case types and calendars",
        "privacy-officer": "Handles data-subject requests and erasure",
        "pii-reader": "May see personal data fields in tool outputs",
        "kill-switch-operator": "May use the organisation's emergency stop",
        "tenant-admin": "Administers the organisation: settings, users and roles",
        "rules-admin": "Writes the organisation's own rules; a second person approves each change",
        auditor: "Reads the organisation's audit trail",
        operator: "Operator console: cost, quotas and incidents",
        executive: "Executive dashboards",
        "channel-agent": "Takes over conversations handed over by agents",
        "channel-supervisor": "Supervises the channels desk",
        "channel-admin": "Configures channels, widgets and quiet hours",
        "channel-consent-admin": "Records and revokes outbound contact consents",
        "channel-campaigner": "Requests proactive outbound notifications",
        "platform-admin": "Platform-wide administration across organisations",
        "platform-auditor": "Reads every organisation's audit trail; exports evidence",
        "tool-admin": "Manages the platform tool registry",
        "code-runner": "Agents acting for this person may run Python in the Code runner",
        "marketplace-curator": "Certifies marketplace listings",
      },
    },
    tools: {
      use: {
        who: "Who may use it:",
        everyOrg: "every organisation's agents, once an agent is allowed it (organisations can restrict it with their own rules).",
        agents: "Used by:",
        none: "no agent yet.",
        agentsHidden: "only platform admins and auditors can see which agents use it.",
        signIn: "Sign-in details last changed:",
      },
      intro: "Systems every organisation's agents can use. Tools from outside companies must pass at least the standard check (the Marketplace in Governance, technical view) before builders can use them.",
      list: "Platform tool registry",
      name: "Tool",
      owner: "Owner",
      status: "Status",
      certification: "Certification",
      version: "Latest version",
      thirdParty: "Third party",
      endpoint: "Endpoint",
      actions: "Actions",
      none: "No tools are registered yet.",
      cert: { uncertified: "Not checked yet", bronze: "Basic check passed", silver: "Standard check passed", gold: "Thorough check passed" },
      statuses: { active: "Active", suspended: "Suspended", deprecated: "Deprecated", deprecated_blocked: "Blocked", revoked: "Revoked", retired: "Retired" },
      import: "Add tools from a file",
      importTitle: "Add tools from a file",
      importHelp: "Choose the tool list file your engineers gave you (.json), or paste its contents.",
      exportLabel: "Tool list (JSON file contents)",
      chooseFile: "Choose a JSON file",
      versionLabel: "Version",
      ownerLabel: "Owner",
      importOk: "Import",
      imported: "{{n}} tool(s) imported",
      result: "Import result",
      created: "Created",
      close: "Close",
      change: "Change status of {{name}}",
      do: { suspend: "Suspend", reactivate: "Reactivate", deprecate: "Deprecate", block: "Block", revoke: "Revoke", retire: "Retire" },
      confirmTitle: "{{action}} tool {{name}}",
      confirmBody: "The change is published to the rules package and takes effect for every organisation. It is audited.",
      noteLabel: "Note (why?)",
      changed: "Tool {{name}} is now {{status}}",
      changedRevision: "Tool {{name}} is now {{status}} (rules package revision {{revision}})",
      publish: "Publish {{name}} to the marketplace",
      publishTitle: "Publish {{name}}@{{version}} to the marketplace",
      publishOk: "Publish",
      titleEn: "Title (English)",
      titleAr: "Title (Arabic)",
      descEn: "Description (English)",
      descAr: "Description (Arabic)",
      published: "{{name}} is listed in the marketplace; a curator can now certify it",
    },
    tenant: {
      pick: "Organisation",
      residencyRegion: "Where your data is kept",
      residencyPlaceholder: "Choose a region",
      residencyRegions: "Regions where AI models may process your requests",
      onlineModels: "Allow online (hosted) models",
      allowedProviders: "Allowed model providers (empty = any approved)",
      maxClassification: "Most sensitive data your organisation may handle",
      noCeiling: "No ceiling",
      timezone: "Time zone",
      saved: "Settings of {{tenant}} saved",
      updated: "Last changed by {{by}} on {{at}}",
      defaults: "Defaults (never changed)",
      typeAndEnter: "Type a value and press Enter",
    },
    web: {
      intro: "The websites your organisation's agents may open with the web browser, and what they may do there. A second organisation admin approves every new website; removing one takes effect at once. Every change is recorded in the audit log.",
      platformTeam: "Your organisation is the platform team: your agents may open the websites on the platform's own list, set in Internet access.",
      openInternet: "Open Internet access",
      add: "Add a website",
      current: "Allowed and waiting",
      history: "Earlier requests ({{n}})",
      none: "No websites yet. Agents cannot open any website until one is added and approved.",
      site: "Website",
      can: "What agents may do",
      why: "Why",
      sep: ", ",
      by: { requested: "Asked by", allowed: "Approved by", rejected: "Turned down by", removed: "Removed by", withdrawn: "Withdrawn by" },
      status: { pending: "Waiting for approval", allowed: "Allowed", rejected: "Turned down", withdrawn: "Withdrawn", removed: "Removed" },
      action: { read: "Read pages", search: "Search the site", forms: "Fill in and send forms", download: "Download files" },
      actionHelp: {
        read: "Open pages and read them. Always included.",
        search: "Use the site's own search box.",
        forms: "Send forms on this website. That changes something there, so your approval rules apply (usually a person approves first).",
        download: "Keep files from the site in Documents: PDF, CSV, text, Excel, Word and pictures, up to 5 MB.",
      },
      reach: {
        needs_platform: "Not open yet: the platform team must add it to the platform's list.",
        needs_network: "Not open yet: the platform's network does not let it through yet. Ask the platform team.",
      },
      approve: "Approve",
      reject: "Turn down",
      remove: "Remove",
      withdraw: "Withdraw",
      yourRequest: "Your request: a second organisation admin approves it.",
      form: {
        title: "Add a website",
        host: "Website address",
        hostHelp: "For example www.gov.ae. Paste a full link and only the website is kept.",
        hostInvalid: "{{value}} is not a website address.",
        reason: "Why do agents need it?",
        reasonHelp: "Shown to the admin who approves it and kept in the audit log.",
        actions: "What may agents do there?",
        searchUrl: "The site's search address",
        searchUrlHelp: "Search for anything on the site, copy the address of the results page and put {query} where your words were. For example https://www.gov.ae/en/search?q={query}",
        submit: "Ask for approval",
        sent: "Sent for approval: a second organisation admin approves it.",
      },
      decide: { approveTitle: "Approve {{host}}?", rejectTitle: "Turn down {{host}}?", note: "Note (optional)", approved: "{{host}} is allowed. Agents can open it now.", rejected: "{{host}} was turned down." },
      removeForm: { title: "Remove {{host}}?", titlePending: "Withdraw the request for {{host}}?", reason: "Why?", done: "{{host}} was removed. Agents can no longer open it.", withdrawn: "The request was withdrawn." },
    },
    internet: {
      intro: "Everything the platform may reach on the internet, in one place. Nothing else leaves the network.",
      movedHere: "Internet addresses for online AI models are set in Administration › Internet access.",
      models: {
        title: "Online AI models",
        help: "The addresses of online AI services (for example api.groq.com) the platform may call. Only the platform team changes this list; each organisation still decides whether it uses online models and up to which data level.",
        platformOnly: "Only platform administrators see and change this list.",
      },
      websites: {
        title: "Websites the web browser may open",
        help: "Agents that use a web browser tool can open only these sites; every page is checked before it opens.",
        none: "No web browser tool is set up.",
      },
    },
    platform: {
      intro: "Platform-wide settings apply to every organisation. Each organisation may make the approval amounts stricter for itself (Administration › Approval amounts); amounts are in each organisation's own currency.",
      minTier: "Always ask for approval from risk level",
      thresholds: "When to ask a person for approval, by risk level",
      tier: "Risk level {{tier}}",
      noLimit: "Not set",
      amount: "Ask for approval above",
      confidence: "…or when the agent is less sure than (%)",
      onlineMax: "Highest classification allowed on online models",
      egress: "Allowed internet addresses (host names)",
      rows: "Most rows an agent may read from business data at once",
      mcp: "Approved local MCP servers",
      mcpIntro: "MCP servers that run on this platform as programs. Each runs in a locked sandbox when an agent uses it: no files, no secrets, no internet unless allowed. Only images already loaded on the platform (pinned by digest) can run. Tool administrators then connect them in Connect a system.",
      mcpNone: "No local MCP server is approved.",
      mcpAdd: "Approve a local server",
      mcpRemove: "Remove {{name}}",
      mcpName: "Name",
      mcpTitle: "Title shown to people",
      mcpImage: "Image (pinned by digest)",
      mcpCommand: "Command inside the image",
      mcpInternet: "May use the internet (through the allow-list proxy)",
      mcpMemory: "Memory (MB)",
      saved: "Platform settings saved",
      readOnly: "Only platform admins can change these settings.",
    },
    classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    display: {
      open: "Display",
      openFor: "Display — how {{name}}'s results are shown",
      title: "How results are shown — {{name}}",
      intro: "Choose how the console draws what each action returns: a table, a record card, a map, a timeline and so on. Your choice wins over the system's own; with neither, the console decides from the shape of the result. This changes only how results look, never what the system does or who may see it.",
      actions: "Actions",
      source: { admin: "Your setting", server: "From the system", inferred: "Automatic" },
      kind: "Show as",
      kinds: { table: "Table", record: "Record card", kv: "Details", list: "List", map: "Map", timeline: "Timeline", network: "Connections", form: "Form", document: "Document", chart: "Chart" },
      titleEn: "Title (English)",
      titleAr: "Title (Arabic)",
      rows: "Where the rows are",
      rowsHelp: "Leave empty when the result is the list itself; otherwise the field that holds it, e.g. items or data.orders.",
      columns: "Columns",
      field: "Field",
      labelEn: "Label (English)",
      labelAr: "Label (Arabic)",
      type: "Type",
      types: { text: "Text", number: "Number", date: "Date", datetime: "Date and time", status: "Status", entity: "Person or record", money: "Money", link: "Link" },
      addColumn: "Add a column",
      remove: "Remove",
      status: "Status colours",
      statusField: "Status field",
      value: "Value",
      tone: "Colour",
      tones: { success: "Green", warning: "Amber", error: "Red", info: "Blue", default: "Grey" },
      addTone: "Add a value",
      places: "Places on a map",
      placesNone: "None",
      placesLatLng: "Latitude and longitude fields",
      placesGeojson: "A GeoJSON field",
      lat: "Latitude field",
      lng: "Longitude field",
      geojson: "GeoJSON field",
      timeField: "Date field for a timeline",
      advanced: "Advanced (JSON)",
      advancedHelp: "Every display option. Changes here update the form when they are valid JSON.",
      jsonInvalid: "This is not valid JSON yet; the form keeps the last valid version.",
      sample: "Sample result",
      sampleHelp: "Paste what the action returns (JSON) to see the result as people will. The sample stays in your browser.",
      sampleInvalid: "The sample is not valid JSON.",
      suggest: "Suggest from this sample",
      preview: "Preview",
      previewEmpty: "Paste a sample result to see a preview.",
      save: "Save",
      saved: "Saved: {{action}} is shown as {{kind}}.",
      reset: "Use the system's own",
      resetDone: "{{action}} is shown as the system says again.",
      systemSays: "The system declares: {{kind}}",
      systemSaysNone: "The system declares nothing; the console decides from the shape.",
      noActions: "This tool has no actions yet.",
      close: "Close",
      issues: {
        kind: "Choose how to show the result.",
        path_rows: "Where the rows are: use field names joined by dots, e.g. data.orders.",
        path_geojson: "GeoJSON field: use field names joined by dots.",
        geo_pair: "Give both the latitude and the longitude field.",
        status_field: "Give the status field, or remove the status colours.",
        column_field: "Every column needs a field.",
        too_big: "The display is too large; remove some options.",
      },
    },
  },
  ar: {
    title: "الإدارة",
    subtitle: "للمنصة كلها: الأدوات والأشخاص وأدوارهم وإعدادات الجهات وإعدادات المنصة.",
    orgSubtitle: "لـ{{org}}: الأشخاص وأدوارهم ومبالغ الاعتماد وطريقة التعامل مع بيانات جهتك.",
    platformTeam: "فريق المنصة",
    tab: { tools: "الأدوات", users: "المستخدمون والأدوار", tenant: "الجهة", platform: "إعدادات المنصة", approvals: "مبالغ الاعتماد", spending: "حدود الإنفاق", internet: "الوصول إلى الإنترنت", web: "متصفح الويب", command: "الملفات" },
    yes: "نعم",
    no: "لا",
    save: "حفظ",
    cancel: "إلغاء",
    issues: {
      json_invalid: "ملف التصدير ليس JSON صالحًا",
      tools_missing: "لا يحتوي التصدير على كائن \"tools\" فيه أداة واحدة على الأقل",
      version_invalid: "يجب أن يكون الإصدار بصيغة 1.0.0",
      owner_required: "المالك مطلوب",
      note_short: "اكتب ملاحظة من ثلاثة أحرف على الأقل",
      title_required: "أدخل العنوان بالعربية والإنجليزية",
      host_invalid: "عناصر السماح بالخروج أسماء مضيفين بأحرف صغيرة دون بروتوكول أو منفذ أو مسار: {{host}}",
      mcp_name_invalid: "يحتاج كل خادم MCP محلي إلى اسم قصير: أحرف إنجليزية صغيرة وأرقام و-",
      mcp_name_duplicate: "خادمان محليان لهما الاسم نفسه",
      mcp_image_invalid: "يجب تثبيت صورة خادم MCP المحلي بالبصمة (…@sha256: و64 حرفًا)",
      mcp_command_missing: "يحتاج كل خادم MCP محلي إلى أمر تشغيله",
      tier_invalid: "يجب أن يكون الحد الأدنى لمستوى الاعتماد من 1 إلى 4",
      rows_invalid: "يجب أن يكون حد صفوف الاسترجاع عددًا صحيحًا من 1 إلى 10000",
      threshold_invalid: "العتبات: يجب أن يكون المبلغ صفرًا أو أكثر والثقة بين 0 و1",
      username_invalid: "اسم المستخدم: من 3 إلى 63 حرفًا لاتينيًا صغيرًا أو رقمًا أو نقطة أو شرطة، يبدأ بحرف أو رقم",
      email_invalid: "أدخل عنوان بريد إلكتروني صالحًا",
      name_required: "أدخل الاسم الأول واسم العائلة",
    },
    users: {
      showDisabled: "عرض الحسابات الموقوفة",
      intro: "الأشخاص الذين يسجّلون الدخول إلى وحدة التحكم. تسري التغييرات عند تسجيل الدخول التالي للمستخدم؛ ويؤدي سحب الأدوار أو تعطيل الحساب أو إعادة تعيين كلمة المرور إلى تسجيل خروجه. تُفحص كل التغييرات بالسياسات وتُسجَّل في سجل التدقيق.",
      tenant: "الجهة",
      search: "البحث بالاسم أو اسم المستخدم أو البريد الإلكتروني",
      newUser: "مستخدم جديد",
      list: "المستخدمون",
      total: "عدد المستخدمين: {{n}}",
      none: "لا يوجد مستخدمون مطابقون.",
      user: "المستخدم",
      username: "اسم المستخدم",
      email: "البريد الإلكتروني",
      status: "الحالة",
      enabled: "نشط",
      disabled: "معطّل",
      roles: "الأدوار",
      clearance: "مستوى التصريح",
      locale: "اللغة",
      actions: "الإجراءات",
      you: "أنت",
      noRoles: "بلا أدوار",
      edit: "تعديل {{name}}",
      editRoles: "تغيير أدوار {{name}}",
      resetPassword: "إعادة تعيين كلمة مرور {{name}}",
      createTitle: "مستخدم جديد",
      editTitle: "تعديل {{name}}",
      rolesTitle: "أدوار {{name}}",
      resetTitle: "إعادة تعيين كلمة مرور {{name}}",
      firstName: "الاسم الأول",
      lastName: "اسم العائلة",
      usernameHelp: "بأحرف صغيرة؛ لا يمكن تغييره لاحقًا.",
      enabledLabel: "الحساب نشط",
      localeName: { ar: "العربية", en: "الإنجليزية" },
      create: "إنشاء المستخدم",
      save: "حفظ",
      created: "تم إنشاء المستخدم {{name}}",
      saved: "تم حفظ المستخدم {{name}}",
      rolesSaved: "تم حفظ أدوار {{name}}",
      initialRoles: "الأدوار",
      passwordTitle: "كلمة المرور المؤقتة لـ {{name}}",
      passwordOnce: "تُعرض مرة واحدة فقط. سلّمها للمستخدم عبر قناة آمنة؛ وعليه اختيار كلمة مرور جديدة عند تسجيل الدخول التالي.",
      copy: "نسخ كلمة المرور",
      copied: "تم نسخ كلمة المرور",
      done: "تم",
      resetBody: "تُنشأ كلمة مرور مؤقتة جديدة، ويُسجَّل خروج المستخدم من جميع الجلسات، وعليه اختيار كلمة مرور جديدة عند تسجيل الدخول التالي.",
      resetOk: "إعادة تعيين كلمة المرور",
      selfRoles: "لا يمكنك تغيير أدوارك بنفسك؛ اطلب ذلك من مسؤول آخر.",
      selfDisable: "لا يمكنك تعطيل حسابك بنفسك.",
      selfReset: "غيّر كلمة مرورك من وحدة إدارة الحساب.",
      platformOnly: "لا يمنح أدوار المنصة إلا مسؤول المنصة، ولمستخدمي جهة المنصة فقط.",
      otherRoles: "يحمل أيضًا (لا تُدار هنا): {{roles}}",
      signOutNote: "يؤدي سحب أي دور إلى تسجيل خروج المستخدم من جميع الجلسات.",
      area: { work: "العمل", build: "البناء", govern: "الحوكمة", observe: "المراقبة", channels: "القنوات", platform: "المنصة" },
      roleName: {
        approver: "معتمِد",
        reviewer: "مراجع",
        "case-worker": "موظف حالات",
        "case-supervisor": "مشرف حالات",
        finance: "فريق المالية",
        "finance-approver": "معتمِد مالي",
        "finance-director": "مدير مالي",
        cfo: "المدير المالي التنفيذي",
        builder: "منشئ وكلاء",
        "tool-owner": "مالك أداة",
        "retrieval-curator": "أمين المعرفة",
        "eval-curator": "أمين الاختبارات",
        "memory-curator": "أمين الذاكرة",
        "data-steward": "أمين البيانات",
        "release-approver": "معتمِد الإصدارات",
        "agent-admin": "مسؤول الوكلاء",
        "model-admin": "مسؤول النماذج",
        "guardrails-admin": "مسؤول الضوابط الوقائية",
        "case-admin": "مسؤول الحالات",
        "privacy-officer": "مسؤول الخصوصية",
        "pii-reader": "قارئ البيانات الشخصية",
        "kill-switch-operator": "مشغّل الإيقاف الطارئ",
        "tenant-admin": "مسؤول الجهة",
        "rules-admin": "مسؤول القواعد",
        auditor: "مدقق",
        operator: "مشغّل",
        executive: "تنفيذي",
        "channel-agent": "موظف دعم",
        "channel-supervisor": "مشرف دعم",
        "channel-admin": "مسؤول القنوات",
        "channel-consent-admin": "مسؤول الموافقات",
        "channel-campaigner": "مرسل الإشعارات",
        "platform-admin": "مسؤول المنصة",
        "platform-auditor": "مدقق المنصة",
        "tool-admin": "مسؤول الأدوات",
        "code-runner": "مستخدم مشغّل الشيفرة",
        "marketplace-curator": "أمين السوق",
      },
      roleDesc: {
        approver: "يوقّع الاعتمادات البشرية في صندوق المراجعة",
        reviewer: "يعالج صندوق المراجعة والحالات",
        "case-worker": "يعالج الحالات والمهام",
        "case-supervisor": "يشرف على قوائم الانتظار ويعيد إسناد المهام ويبتّ فيها جماعيًا",
        finance: "دور أعمال نموذجي يستخدمه الوكلاء المرجعيون",
        "finance-approver": "يعتمد الإجراءات المالية للوكلاء المرجعيين",
        "finance-director": "يوقّع المستوى الأوسط من الاعتمادات الكبيرة",
        cfo: "يوقّع المستوى الأعلى من الاعتمادات الكبيرة",
        builder: "يبني الوكلاء ويختبرهم ويطلب إطلاقهم",
        "tool-owner": "يملك تسجيلات الأدوات",
        "retrieval-curator": "يدير مجموعات المعرفة",
        "eval-curator": "يحوّل الملاحظات إلى مجموعات بيانات للتقييم",
        "memory-curator": "يكتب المعرفة والذاكرة على مستوى الجهة",
        "data-steward": "يطّلع على جميع صفوف بيانات العمل داخل الجهة",
        "release-approver": "يعتمد إطلاق الإصدارات (مبدأ الأعين الأربع)",
        "agent-admin": "يدير هويات الوكلاء",
        "model-admin": "النماذج: المزوّدون والتوجيه وحدود الإنفاق",
        "guardrails-admin": "يدير إعدادات الضوابط الوقائية",
        "case-admin": "يدير أنواع الحالات والتقاويم",
        "privacy-officer": "يعالج طلبات أصحاب البيانات وطلبات المحو",
        "pii-reader": "يطّلع على حقول البيانات الشخصية في مخرجات الأدوات",
        "kill-switch-operator": "يستخدم الإيقاف الطارئ في الجهة",
        "tenant-admin": "يدير الجهة: الإعدادات والمستخدمين والأدوار",
        "rules-admin": "يكتب قواعد الجهة الخاصة، ويعتمد شخص ثانٍ كل تغيير",
        auditor: "يقرأ سجل التدقيق الخاص بالجهة",
        operator: "لوحة التشغيل: التكلفة والحصص والحوادث",
        executive: "لوحات المعلومات التنفيذية",
        "channel-agent": "يتولى المحادثات التي يحيلها الوكلاء",
        "channel-supervisor": "يشرف على مكتب القنوات",
        "channel-admin": "يهيئ القنوات والأدوات المضمّنة وساعات الهدوء",
        "channel-consent-admin": "يسجّل موافقات التواصل الصادر ويلغيها",
        "channel-campaigner": "يطلب إرسال إشعارات صادرة استباقية",
        "platform-admin": "إدارة المنصة على مستوى جميع الجهات",
        "platform-auditor": "يقرأ سجلات تدقيق جميع الجهات ويصدّر الأدلة",
        "tool-admin": "يدير سجل أدوات المنصة",
        "code-runner": "يمكن للوكلاء الذين يعملون نيابةً عنه تشغيل Python في مشغّل الشيفرة",
        "marketplace-curator": "يعتمد قوائم السوق",
      },
    },
    tools: {
      use: {
        who: "من يمكنه استخدامه:",
        everyOrg: "وكلاء كل الجهات، بعد السماح للوكيل باستخدامه (ويمكن لكل جهة تقييده بقواعدها).",
        agents: "يستخدمه:",
        none: "لا يوجد وكيل بعد.",
        agentsHidden: "يرى مسؤولو المنصة ومدققوها فقط الوكلاء الذين يستخدمونه.",
        signIn: "آخر تغيير لبيانات الدخول:",
      },
      intro: "أنظمة يستطيع وكلاء كل مؤسسة استخدامها. يجب أن تجتاز أدوات الشركات الخارجية الفحص القياسي على الأقل (السوق في الحوكمة، العرض التقني) قبل أن يستخدمها المنشئون.",
      list: "سجل أدوات المنصة",
      name: "الأداة",
      owner: "المالك",
      status: "الحالة",
      certification: "الاعتماد",
      version: "أحدث إصدار",
      thirdParty: "طرف خارجي",
      endpoint: "نقطة الاتصال",
      actions: "الإجراءات",
      none: "لم تُسجَّل أي أداة بعد.",
      cert: { uncertified: "لم تُفحص بعد", bronze: "اجتازت الفحص الأساسي", silver: "اجتازت الفحص القياسي", gold: "اجتازت الفحص الشامل" },
      statuses: { active: "نشطة", suspended: "موقوفة", deprecated: "مهملة", deprecated_blocked: "محظورة", revoked: "ملغاة", retired: "مُتقاعدة" },
      import: "إضافة أدوات من ملف",
      importTitle: "إضافة أدوات من ملف",
      importHelp: "اختر ملف قائمة الأدوات الذي أعطاك إياه المهندسون (‎.json)، أو الصق محتواه.",
      exportLabel: "قائمة الأدوات (محتوى ملف JSON)",
      chooseFile: "اختيار ملف JSON",
      versionLabel: "الإصدار",
      ownerLabel: "المالك",
      importOk: "استيراد",
      imported: "تم استيراد {{n}} أداة",
      result: "نتيجة الاستيراد",
      created: "أُنشئت",
      close: "إغلاق",
      change: "تغيير حالة {{name}}",
      do: { suspend: "إيقاف", reactivate: "إعادة تفعيل", deprecate: "إهمال", block: "حظر", revoke: "إلغاء نهائي", retire: "إحالة إلى التقاعد" },
      confirmTitle: "{{action}} الأداة {{name}}",
      confirmBody: "يُنشر التغيير في حزمة القواعد ويسري على جميع الجهات، ويُسجَّل في سجل التدقيق.",
      noteLabel: "ملاحظة (السبب)",
      changed: "أصبحت حالة الأداة {{name}}: {{status}}",
      changedRevision: "أصبحت حالة الأداة {{name}}: {{status}} (مراجعة حزمة القواعد {{revision}})",
      publish: "نشر {{name}} في السوق",
      publishTitle: "نشر {{name}}@{{version}} في السوق",
      publishOk: "نشر",
      titleEn: "العنوان (بالإنجليزية)",
      titleAr: "العنوان (بالعربية)",
      descEn: "الوصف (بالإنجليزية)",
      descAr: "الوصف (بالعربية)",
      published: "أُدرجت {{name}} في السوق؛ يمكن لأمين السوق اعتمادها الآن",
    },
    tenant: {
      pick: "الجهة",
      residencyRegion: "مكان حفظ بياناتك",
      residencyPlaceholder: "اختر منطقة",
      residencyRegions: "المناطق التي يُسمح فيها لنماذج الذكاء الاصطناعي بمعالجة طلباتك",
      onlineModels: "السماح بالنماذج المستضافة عبر الإنترنت",
      allowedProviders: "مزوّدو النماذج المسموح بهم (فارغ = أي مزوّد معتمد)",
      maxClassification: "أعلى درجة حساسية للبيانات التي تتعامل معها جهتك",
      noCeiling: "بلا سقف",
      timezone: "المنطقة الزمنية",
      saved: "تم حفظ إعدادات {{tenant}}",
      updated: "آخر تغيير بواسطة {{by}} في {{at}}",
      defaults: "القيم الافتراضية (لم تتغير)",
      typeAndEnter: "اكتب قيمة ثم اضغط Enter",
    },
    web: {
      intro: "المواقع التي يجوز لوكلاء جهتك فتحها بمتصفح الويب، وما يجوز لهم فعله فيها. يعتمد مسؤول ثانٍ في الجهة كل موقع جديد، ويسري حذف الموقع فورًا. ويُسجَّل كل تغيير في سجل التدقيق.",
      platformTeam: "جهتك هي فريق المنصة: يجوز لوكلائك فتح المواقع الواردة في قائمة المنصة نفسها، وتُضبط في «الوصول إلى الإنترنت».",
      openInternet: "افتح «الوصول إلى الإنترنت»",
      add: "إضافة موقع",
      current: "المسموح بها والمنتظرة",
      history: "الطلبات السابقة ({{n}})",
      none: "لا توجد مواقع بعد. لا يستطيع الوكلاء فتح أي موقع حتى يُضاف موقع ويُعتمد.",
      site: "الموقع",
      can: "ما يجوز للوكلاء فعله",
      why: "السبب",
      sep: "، ",
      by: { requested: "طلبه", allowed: "اعتمده", rejected: "رفضه", removed: "حذفه", withdrawn: "سحبه" },
      status: { pending: "بانتظار الاعتماد", allowed: "مسموح به", rejected: "مرفوض", withdrawn: "مسحوب", removed: "محذوف" },
      action: { read: "قراءة الصفحات", search: "البحث في الموقع", forms: "تعبئة النماذج وإرسالها", download: "تنزيل الملفات" },
      actionHelp: {
        read: "فتح الصفحات وقراءتها. مشمول دائمًا.",
        search: "استخدام مربع البحث الخاص بالموقع.",
        forms: "إرسال النماذج في هذا الموقع. وهذا يغيّر شيئًا هناك، لذا تنطبق قواعد الاعتماد لديك (عادة يعتمد شخص أولًا).",
        download: "حفظ ملفات من الموقع في المستندات: PDF وCSV والنصوص وExcel وWord والصور، حتى 5 ميغابايت.",
      },
      reach: {
        needs_platform: "غير مفتوح بعد: يجب أن يضيفه فريق المنصة إلى قائمة المنصة.",
        needs_network: "غير مفتوح بعد: شبكة المنصة لا تسمح بالوصول إليه بعد. اطلب ذلك من فريق المنصة.",
      },
      approve: "اعتماد",
      reject: "رفض",
      remove: "حذف",
      withdraw: "سحب",
      yourRequest: "طلبك: يعتمده مسؤول ثانٍ في الجهة.",
      form: {
        title: "إضافة موقع",
        host: "عنوان الموقع",
        hostHelp: "مثل www.gov.ae. يمكنك لصق رابط كامل وسيُحتفظ بالموقع فقط.",
        hostInvalid: "‏{{value}} ليس عنوان موقع.",
        reason: "لماذا يحتاجه الوكلاء؟",
        reasonHelp: "يظهر للمسؤول الذي يعتمده ويُحفظ في سجل التدقيق.",
        actions: "ما الذي يجوز للوكلاء فعله هناك؟",
        searchUrl: "عنوان البحث في الموقع",
        searchUrlHelp: "ابحث عن أي شيء في الموقع، وانسخ عنوان صفحة النتائج، وضع {query} مكان كلماتك. مثال: https://www.gov.ae/en/search?q={query}",
        submit: "طلب الاعتماد",
        sent: "أُرسل للاعتماد: يعتمده مسؤول ثانٍ في الجهة.",
      },
      decide: { approveTitle: "اعتماد {{host}}؟", rejectTitle: "رفض {{host}}؟", note: "ملاحظة (اختيارية)", approved: "أصبح {{host}} مسموحًا به. يستطيع الوكلاء فتحه الآن.", rejected: "رُفض {{host}}." },
      removeForm: { title: "حذف {{host}}؟", titlePending: "سحب طلب {{host}}؟", reason: "لماذا؟", done: "حُذف {{host}}. لم يعد الوكلاء يستطيعون فتحه.", withdrawn: "سُحب الطلب." },
    },
    internet: {
      intro: "كل ما يمكن للمنصة الوصول إليه على الإنترنت في مكان واحد. لا يخرج شيء آخر من الشبكة.",
      movedHere: "تُضبط عناوين الإنترنت لنماذج الذكاء الاصطناعي عبر الإنترنت في الإدارة › الوصول إلى الإنترنت.",
      models: {
        title: "نماذج الذكاء الاصطناعي عبر الإنترنت",
        help: "عناوين خدمات الذكاء الاصطناعي عبر الإنترنت (مثل api.groq.com) التي يمكن للمنصة الاتصال بها. لا يغيّر هذه القائمة إلا فريق المنصة؛ وتقرر كل جهة مع ذلك هل تستخدم النماذج عبر الإنترنت وحتى أي مستوى بيانات.",
        platformOnly: "لا يرى هذه القائمة ولا يغيّرها إلا مسؤولو المنصة.",
      },
      websites: {
        title: "المواقع التي يمكن لمتصفح الويب فتحها",
        help: "لا يستطيع الوكلاء الذين يستخدمون أداة متصفح الويب فتح غير هذه المواقع؛ وتُفحص كل صفحة قبل فتحها.",
        none: "لا توجد أداة متصفح ويب مُعدّة.",
      },
    },
    platform: {
      intro: "تسري إعدادات المنصة على جميع الجهات. يمكن لكل جهة جعل مبالغ الاعتماد أشد لنفسها (الإدارة › مبالغ الاعتماد)؛ والمبالغ بعملة كل جهة.",
      minTier: "اطلب الاعتماد دائمًا ابتداءً من مستوى الخطورة",
      thresholds: "متى يُطلب اعتماد شخص، حسب مستوى الخطورة",
      tier: "مستوى الخطورة {{tier}}",
      noLimit: "غير محدد",
      amount: "اطلب الاعتماد فوق",
      confidence: "…أو عندما تقل ثقة الوكيل عن (%)",
      onlineMax: "أعلى تصنيف مسموح به للنماذج المستضافة",
      egress: "عناوين الإنترنت المسموحة (أسماء المضيفين)",
      rows: "أقصى عدد من الصفوف يقرؤه الوكيل من بيانات العمل دفعة واحدة",
      mcp: "خوادم MCP المحلية المعتمدة",
      mcpIntro: "خوادم MCP تعمل على هذه المنصة كبرامج. يعمل كل منها في بيئة معزولة ومقفلة عندما يستخدمه وكيل: لا ملفات ولا أسرار ولا إنترنت إلا إذا سُمح بذلك. تعمل فقط الصور المحمّلة مسبقًا على المنصة (المثبتة بالبصمة). ثم يربطها مسؤولو الأدوات في ربط نظام.",
      mcpNone: "لا يوجد خادم MCP محلي معتمد.",
      mcpAdd: "اعتماد خادم محلي",
      mcpRemove: "إزالة {{name}}",
      mcpName: "الاسم",
      mcpTitle: "العنوان الظاهر للناس",
      mcpImage: "الصورة (مثبتة بالبصمة)",
      mcpCommand: "الأمر داخل الصورة",
      mcpInternet: "يمكنه استخدام الإنترنت (عبر وكيل القائمة المسموح بها)",
      mcpMemory: "الذاكرة (ميغابايت)",
      saved: "تم حفظ إعدادات المنصة",
      readOnly: "لا يغيّر هذه الإعدادات إلا مسؤولو المنصة.",
    },
    classification: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيّد" },
    display: {
      open: "العرض",
      openFor: "العرض — طريقة عرض نتائج {{name}}",
      title: "طريقة عرض النتائج — {{name}}",
      intro: "اختر كيف تعرض وحدة التحكم ما يُرجعه كل إجراء: جدول أو بطاقة سجل أو خريطة أو تسلسل زمني وغير ذلك. يتقدّم اختيارك على ما يحدده النظام نفسه، وإن لم يوجد أيٌّ منهما تقرّر وحدة التحكم من شكل النتيجة. هذا يغيّر شكل النتائج فقط، لا ما يفعله النظام ولا من يُسمح له برؤيتها.",
      actions: "الإجراءات",
      source: { admin: "إعدادك", server: "من النظام", inferred: "تلقائي" },
      kind: "اعرضها كـ",
      kinds: { table: "جدول", record: "بطاقة سجل", kv: "تفاصيل", list: "قائمة", map: "خريطة", timeline: "تسلسل زمني", network: "روابط", form: "نموذج", document: "مستند", chart: "رسم بياني" },
      titleEn: "العنوان (بالإنجليزية)",
      titleAr: "العنوان (بالعربية)",
      rows: "مكان الصفوف",
      rowsHelp: "اتركه فارغاً إذا كانت النتيجة هي القائمة نفسها، وإلا فاكتب الحقل الذي يحويها، مثل items أو data.orders.",
      columns: "الأعمدة",
      field: "الحقل",
      labelEn: "الاسم (بالإنجليزية)",
      labelAr: "الاسم (بالعربية)",
      type: "النوع",
      types: { text: "نص", number: "رقم", date: "تاريخ", datetime: "تاريخ ووقت", status: "حالة", entity: "شخص أو سجل", money: "مبلغ", link: "رابط" },
      addColumn: "إضافة عمود",
      remove: "إزالة",
      status: "ألوان الحالة",
      statusField: "حقل الحالة",
      value: "القيمة",
      tone: "اللون",
      tones: { success: "أخضر", warning: "كهرماني", error: "أحمر", info: "أزرق", default: "رمادي" },
      addTone: "إضافة قيمة",
      places: "الأماكن على الخريطة",
      placesNone: "لا يوجد",
      placesLatLng: "حقلا خط العرض وخط الطول",
      placesGeojson: "حقل GeoJSON",
      lat: "حقل خط العرض",
      lng: "حقل خط الطول",
      geojson: "حقل GeoJSON",
      timeField: "حقل التاريخ للتسلسل الزمني",
      advanced: "متقدم (JSON)",
      advancedHelp: "جميع خيارات العرض. التعديلات هنا تحدّث النموذج متى كانت JSON صحيحاً.",
      jsonInvalid: "هذا ليس JSON صحيحاً بعد؛ يحتفظ النموذج بآخر نسخة صحيحة.",
      sample: "نتيجة نموذجية",
      sampleHelp: "الصق ما يُرجعه الإجراء (JSON) لترى النتيجة كما سيراها الناس. تبقى النتيجة في متصفحك.",
      sampleInvalid: "النتيجة النموذجية ليست JSON صحيحاً.",
      suggest: "اقتراح من هذه النتيجة",
      preview: "معاينة",
      previewEmpty: "الصق نتيجة نموذجية لترى المعاينة.",
      save: "حفظ",
      saved: "تم الحفظ: يُعرض {{action}} كـ{{kind}}.",
      reset: "استخدام ما يحدده النظام",
      resetDone: "يُعرض {{action}} كما يحدده النظام مجدداً.",
      systemSays: "يحدد النظام: {{kind}}",
      systemSaysNone: "لا يحدد النظام شيئاً؛ تقرّر وحدة التحكم من شكل النتيجة.",
      noActions: "لا توجد إجراءات لهذه الأداة بعد.",
      close: "إغلاق",
      issues: {
        kind: "اختر طريقة عرض النتيجة.",
        path_rows: "مكان الصفوف: استخدم أسماء حقول تفصلها نقاط، مثل data.orders.",
        path_geojson: "حقل GeoJSON: استخدم أسماء حقول تفصلها نقاط.",
        geo_pair: "اكتب حقلي خط العرض وخط الطول معاً.",
        status_field: "اكتب حقل الحالة، أو أزل ألوان الحالة.",
        column_field: "كل عمود يحتاج إلى حقل.",
        too_big: "العرض كبير جداً؛ أزل بعض الخيارات.",
      },
    },
  },
};
